/** @jsxImportSource @emotion/react */
import React, { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { Button, Checkbox } from '@mui/material';
import { IconButton } from 'components/buttons/IconSVG';
import { findMpProgressSummaryPlnt } from 'apis/mp/MpTracking';

import {
  FlexChart,
  FlexChartLegend,
  FlexChartSeries,
  FlexChartAxis,
} from '@grapecity/wijmo.react.chart';
import { FlexChartAnimation } from '@grapecity/wijmo.react.chart.animation';
import { CellMaker } from '@grapecity/wijmo.grid.cellmaker';
import {
  InputBox,
  SearchBox,
  SearchBoxRow,
  SearchButtonWrap,
  SearchCols,
  SearchRows,
} from '../../components/layouts/SearchBox';
import { ComboBox } from '../../components/selects/ComboBox';
import { Code } from '../../models/common/CommonCode';
import { getCommonCodeNames, getCommonCodeNamesCondition } from '../../apis/admin/CommonCode';
import { getYearList } from '../../apis/gtng/GatingRegist';
import { DatePickerWrap } from '../../components/inputs/DatePicker';
import Datepicker from 'react-tailwindcss-datepicker';
import { useMessageBar } from '../../hooks/useMessageBar';
import styled from '@emotion/styled';
import { st } from '../../components/inputs/CustomInput';
import _ from 'lodash';
import { ContentSection } from '../../components/layouts/ContentSection';
import CustomGrid from '../../components/grids/CustomGrid';
import SearchBoxButtons from '../../components/buttons/SearchBoxButtons';
import {
  ControlBtnGroup,
  SubTitleGroup,
  SubTitleLayout,
} from '../../components/layouts/ContentLayout';
import * as wjGridXlsx from '@grapecity/wijmo.grid.xlsx';
import { ItemStatCd } from '../../models/mp/MpItem';
export interface condition {
  searchYear?: string;
  perdDivs?: string;
  startDate?: string;
  endDate?: string;
  perdDivsOption?: string;
  dateYn?: string;
}

const MpProgressSummaryPlntPage = () => {
  const { t } = useTranslation();
  const [code, setCode] = useState<any>();
  const [condition, setCondition] = useState<condition>({});
  const [errors, setErrors] = useState<any>({});
  const [isRequiredYear, setRequiredYear] = useState<boolean>();
  const [isRequiredPerDivsOption, setRequiredPerDivsOption] = useState<boolean>();
  const [excelData, setExcelData] = useState<any>();

  const { openMessageBar } = useMessageBar();
  const navigate = useNavigate();

  const defaultItemStatCd = [
    ItemStatCd.CopReview,
    ItemStatCd.M4Change,
    ItemStatCd.HeComplete,
    ItemStatCd.Drop,
  ] as string[];

  // graph
  const [chartData, setChartData] = useState<any>([]);
  const [bindingX, setBindingX] = useState<string>('');
  const [chartHeader, setChartHeader] = useState<string>('');
  const [dynamicCol, setDynamicCol] = useState<any[]>([]);

  const [rowData, setRowData] = useState<any>([]);

  useEffect(() => {
    getCommonCodes();
    handleSearch();
  }, []);

  const chartSeries = useMemo(() => {
    return (code?.mpItemStatCd || []).filter((o) => defaultItemStatCd.includes(o.cmnCd));
  }, [code?.mpItemStatCd]);

  const getCommonCodes = async () => {
    const searchYearGet = await getYearList();
    const perdDivs: Code[] = await getCommonCodeNames('PERD_DIVS'); // 기준 (년/반기/분기/월)
    const mpItemStatCd: Code[] = await getCommonCodeNames('MP_ITEM_STAT_CD');
    const elmCopCd: Code[] = await getCommonCodeNames('ELM_COP_CD'); // 법인코드

    const searchYear = searchYearGet.reduce(
      (acc, cur) => [...acc, { cmnCd: String(cur.STARTYEAR), cmnCdNm: String(cur.STARTYEAR) }],
      [] as Code[]
    );

    setCode({
      ...code,
      searchYear: searchYear,
      perdDivs: perdDivs,
      mpItemStatCd: mpItemStatCd,
      elmCopCd: elmCopCd,
    });
  };

  const handleSearch = () => {
    if (
      !_.isEmpty(condition.perdDivs) &&
      (_.isNull(condition.searchYear) || _.isEmpty(condition.searchYear))
    ) {
      setRequiredYear(true);
      setErrors((prev) => ({
        ...prev,
        searchYear: true,
      }));
      return;
    }
    // 상세 기간 조건을 선택 안할 경우..
    if (
      !_.isEmpty(condition.perdDivs) &&
      condition.perdDivs !== 'YY' &&
      (_.isEmpty(condition.perdDivsOption) || _.isNull(condition.perdDivsOption))
    ) {
      setRequiredPerDivsOption(true);
      setErrors((prev) => ({
        ...prev,
        perdDivsOption: true,
      }));
      return;
    }

    setRowData(null);

    findMpProgressSummaryPlnt(condition).then((result) => {
      if (result !== null) {
        setChartData(result.chart);
        setBindingX(result.itemStatCd);
        setChartHeader(condition.perdDivs || '');

        if (result.chart.length === 0) return;
        // 항목 리스트 추출 (공장명 제외)
        const columnList = Object.keys(result.chart[0]).filter(
          (key) => key !== 'COP_CD' && key !== 'SORT_ORD'
        );
        columnList.sort(
          (a, b) =>
            defaultItemStatCd.indexOf(a.replaceAll("'", '')) -
            defaultItemStatCd.indexOf(b.replaceAll("'", ''))
        );
        const columnListPlnt = result.chart.map((row) => row.COP_CD);

        setDynamicCol(columnListPlnt);

        // 변환
        const transposed = columnList.map((item) => {
          const col =
            (code?.mpItemStatCd || []).find((o) => o.cmnCd === item.replaceAll("'", ''))?.cmnCdNm ||
            item;

          const row = { ['item']: col };

          result.chart.forEach((data) => {
            row[data.COP_CD] = data[item];
          });
          return row;
        });
        setRowData(transposed);
      } else {
        openMessageBar({
          type: 'error',
          content: t(
            'com.msg.요청 정보 조회 중 오류가 발생했습니다.',
            '요청 정보 조회 중 오류가 발생했습니다.'
          ),
        });
      }
    });
  };

  const changePerdDivsOption = async (option?: string) => {
    if (option) {
      console.log('---');
    }
  };

  const changePerdDivs = async (perdDivsVal?: string) => {
    let perdDivsOption = [] as Code[];
    setRequiredPerDivsOption(true);
    switch (perdDivsVal) {
      case 'HLF':
        perdDivsOption = [
          { cmnCd: 'hlf1', cmnCdNm: '상반기' },
          { cmnCd: 'hlf2', cmnCdNm: '하반기' },
        ];
        break;
      case 'QTR':
        perdDivsOption = [
          { cmnCd: 'qtr1', cmnCdNm: '1분기' },
          { cmnCd: 'qtr2', cmnCdNm: '2분기' },
          { cmnCd: 'qtr3', cmnCdNm: '3분기' },
          { cmnCd: 'qtr4', cmnCdNm: '4분기' },
        ];
        break;
      case 'MM':
        perdDivsOption = [
          { cmnCd: '1', cmnCdNm: '1' },
          { cmnCd: '2', cmnCdNm: '2' },
          { cmnCd: '3', cmnCdNm: '3' },
          { cmnCd: '4', cmnCdNm: '4' },
          { cmnCd: '5', cmnCdNm: '5' },
          { cmnCd: '6', cmnCdNm: '6' },
          { cmnCd: '7', cmnCdNm: '7' },
          { cmnCd: '8', cmnCdNm: '8' },
          { cmnCd: '9', cmnCdNm: '9' },
          { cmnCd: '10', cmnCdNm: '10' },
          { cmnCd: '11', cmnCdNm: '11' },
          { cmnCd: '12', cmnCdNm: '12' },
        ];
        break;
      default:
        setRequiredPerDivsOption(false);
        break;
    }

    setCode((prev) => ({
      ...prev,
      perdDivsOption: perdDivsOption,
    }));
  };

  const moveToTracking = (copCd: string) => {
    navigate(`/mp/tracking-management`, {
      state: {
        copCd: copCd,
      },
    });
  };

  const layoutDefinition = useMemo(() => {
    const layout = [] as any[];

    const defaultCols = (code?.elmCopCd || []).map((o) => o.cmnCd);
    const columns = (dynamicCol || []).length < 1 ? defaultCols : dynamicCol;

    (columns || []).forEach((s, idx) => {
      layout.push({
        binding: s,
        header: s,
        align: 'center',
        minWidth: 103,
        width: '*',
        cellTemplate: CellMaker.makeLink({
          click: (e, ctx) => {
            if (ctx && ctx.col.binding) {
              // 수평전개진척현황 화면으로 이동 (법인코드 기본값 설정)
              moveToTracking(ctx.col.binding);
            }
          },
        }),
      });
    });

    return [
      {
        binding: 'item',
        header: t('mp.label.상태', '상태'),
        align: 'center',
        minWidth: 110,
        width: '*',
      },
      ...layout,
    ];
  }, [dynamicCol, code?.elmCopCd]);

  const initialized = (grid) => {
    setExcelData(grid);
    grid.rowHeaders.columns.clear();
  };

  // graph
  const palette = [
    'rgb(173, 206, 255, 0.99)',
    'rgb(252, 169, 191, 0.99)',
    'rgb(241, 244, 243, 0.99)',
    'rgb(218, 240, 136, 0.99)',
  ];

  const handleResetCondition = () => {
    setCondition(
      (prev) => Object.keys(prev).reduce((acc, cur) => Object.assign(acc, { [cur]: '' }), {}) as any
    );
  };

  const onExportXlsxClick = () => {
    wjGridXlsx.FlexGridXlsxConverter.saveAsync(excelData, undefined, '수평전개.xlsx');
  };

  return (
    <>
      <SearchBox>
        <SearchBoxRow>
          <InputBox>
            <SearchRows className="FourCol">
              <SearchCols>
                <label>{String(t('com.label.기준년도', '기준년도'))}</label>
                <ComboBox
                  options={code?.searchYear}
                  disabled={condition.dateYn === 'Y'}
                  placeholder={String(t('com.label.전체', '전체'))}
                  defaultValue={condition.searchYear}
                  msgError={String(
                    t('com.msg.기준년도를 선택해 주세요.', '기준년도를 선택해 주세요.')
                  )}
                  isError={errors?.searchYear}
                  onChange={(value) => {
                    if (isRequiredYear) {
                      setErrors((prev) => ({
                        ...prev,
                        searchYear: false,
                      }));
                    }
                    setCondition({
                      ...condition,
                      searchYear: value,
                    });
                  }}
                />
              </SearchCols>
              <SearchCols>
                <label>
                  <span>{String(t('com.label.기간', '기간'))}</span>
                </label>
                <ComboBox
                  placeholder={String(t('com.label.전체', '전체'))}
                  disabled={condition.dateYn === 'Y'}
                  options={code?.perdDivs}
                  defaultValue={condition?.perdDivs}
                  onChange={(value) => {
                    setCondition({
                      ...condition,
                      perdDivs: value as string,
                    });
                    changePerdDivs(value);
                  }}
                />
                <ComboBox
                  options={code?.perdDivsOption}
                  disabled={condition.dateYn === 'Y'}
                  placeholder={String(t('com.label.선택', '선택'))}
                  defaultValue={condition?.perdDivsOption}
                  msgError={String(
                    t('com.msg.상세기간을 선택해 주세요.', '상세기간을 선택해 주세요.')
                  )}
                  isError={errors?.perdDivsOption}
                  onChange={(value) => {
                    if (isRequiredPerDivsOption) {
                      setErrors((prev) => ({
                        ...prev,
                        perdDivsOption: false,
                      }));
                    }
                    setCondition({
                      ...condition,
                      perdDivsOption: value as string,
                    });
                    // changePerdDivsOption(value);
                  }}
                />
              </SearchCols>
              <SearchCols>
                <label>
                  <Checkbox
                    id="fstrYn"
                    name="fstrYn"
                    css={st.checkbox}
                    className="custom"
                    checked={condition.dateYn === 'Y'}
                    onClick={(e) => {
                      setCondition({
                        ...condition,
                        dateYn: condition.dateYn === 'Y' ? 'N' : 'Y',
                        startDate: '',
                        endDate: '',
                        searchYear: '',
                        perdDivs: '',
                        perdDivsOption: '',
                      });
                      setRequiredYear(false);
                      setRequiredPerDivsOption(false);
                    }}
                    disableRipple
                  />
                  &nbsp; {t('ut.label.직접입력', '직접입력')}
                </label>
                <DatePickerWrap className="range">
                  <Datepicker
                    inputClassName="datepickerInput"
                    disabled={condition.dateYn != 'Y'}
                    value={{
                      startDate: condition?.startDate || null,
                      endDate: condition?.endDate || null,
                    }}
                    onChange={(value) => {
                      setCondition({
                        ...condition,
                        startDate: String(value?.startDate || ''), // newValue.startDate.toString(),
                        endDate: String(value?.endDate || ''), //newValue.endDate.toString(),
                      });
                    }}
                    i18n={'ko'}
                    useRange={true}
                    placeholder="YYYY.MM.DD - YYYY.MM.DD"
                    displayFormat={'YYYY.MM.DD'}
                  ></Datepicker>
                </DatePickerWrap>
              </SearchCols>
            </SearchRows>
          </InputBox>
          <SearchBoxButtons
            // defaultCondition={{
            //   startDataInsDtm: dayjs().add(-2, 'year').format('YYYY.MM.DD'),
            //   endDataInsDtm: dayjs().format('YYYY.MM.DD'),
            // }}
            onReset={handleResetCondition}
            onSearch={handleSearch}
          />
        </SearchBoxRow>
      </SearchBox>

      <ContentSection>
        <FlexChart
          header={String(t('mp.label.수평전개 현황(공장기준)', '수평전개 현황(공장기준)'))}
          bindingX={'COP_CD'}
          selectionMode="Point"
          itemsSource={chartData || []}
          palette={palette}
          style={{ height: '480px' }}
        >
          <FlexChartLegend position="Bottom" />
          {chartSeries.map((o, idx) => (
            <FlexChartSeries key={`series_${idx}`} name={o.cmnCdNm} binding={`'${o.cmnCd}'`} />
          ))}
          <FlexChartAxis wjProperty="axisX" />
          <FlexChartAnimation />
        </FlexChart>
      </ContentSection>
      <SubTitleLayout>
        <SubTitleGroup>
          <h3>{t('mp.label.수평전개 Item', '수평전개 Item')}</h3>
          {/*<span className="total">*/}
          {/*  {t('com.label.총', '총')}*/}
          {/*  <span>{rowData ? rowData.length : 0}</span>*/}
          {/*  {t('com.label.건', '건')}*/}
          {/*</span>*/}
        </SubTitleGroup>
        <ControlBtnGroup>
          <Button
            css={IconButton.button}
            className="Exceldown"
            disableRipple
            onClick={onExportXlsxClick}
          >
            {t('com.button.다운로드', '화면 다운로드')}
          </Button>
        </ControlBtnGroup>
      </SubTitleLayout>
      <CustomGrid
        layoutDefinition={layoutDefinition}
        rowData={rowData}
        height={149}
        initialized={initialized}
        isReadOnly={true}
        isFilter={false}
        isSelector={false}
        allowPinning={false}
        align="center"
      />
    </>
  );
};

export const Chart3Div = styled.div`
  margin-top: 24px;
  display: flex;
  align-items: center;
  justify-content: space-between;

  & > div {
    width: calc((100% - 80px) / 3) !important;
    padding: 0 5px 0 5px;
  }
`;

export default MpProgressSummaryPlntPage;
